import { useEffect } from "react";
import PropTypes from "prop-types";
import { useRouter as useNextRouter } from "next/router";
import cs from "classnames/bind";
import {
  Breadcrumbs,
  LinkGenerator
} from "@lululemon/dsp-ecom-pattern-library";
import { redirect, trackEvent, EVENT_CATEGORY } from "@lululemon/ecom-core";
import { sortCards } from "./util/sort-cards";
import {
  PromoBanner,
  readQuery,
  useWindowDimensions
} from "@lululemon/dsp-legacy-shared";
import { LocaleEnum, graphql } from "@lululemon/dsp-shared";
import { storeMeta } from "./content/generateMetaData";
import { STORE_TOP_SELLERS_BANNER } from "./content/analytics-tags";
import SweatWithUs from "./components/sweat-with-us/sweat-with-us";
import SweatWithUsExperientialStore from "./components/sweat-with-us-experiential-store/sweat-with-us-experiential-store";
import SpacesGrid from "./components/spaces-grid/spaces-grid";
import ContactUs from "./components/contact-us/contact-us";
import styles from "./store.module.scss";
import storeContent from "./content/content";
import cardsQuery from "./graphql/queries/cards";
import carouselQuery from "./graphql/queries/carousel";
import experientialStoreQuery from "./graphql/queries/experiential-store";
import optInFlowQuery from "./graphql/queries/opt-in-flow";
import { fetchStoreBySlug } from "@lululemon/dsp-clients";
import { getSlug } from "@lululemon/dsp-utils";

export const Store = ({ storeData, slug }) => {
  const router = useNextRouter();
  const storeSpacesCount = storeData?.spaces?.length;

  useEffect(() => {
    trackEvent(EVENT_CATEGORY.PAGE_VIEW, {
      headerUnified:
        `${storeData?.country}-${storeData?.city}-${storeData?.name}`
          .replace(/\W/g, "-")
          .toLowerCase(),
      screenType: "stores",
      category: {
        gender: "all"
      }
    });
  }, []);

  // HYDRATION MISMATCH CAUSE
  const getIsMobile = () => {
    if (!process.browser) {
      return false;
    }

    let isMobile = true;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { width } = useWindowDimensions();
    isMobile = width < 768;
    return isMobile;
  };

  const getSlidesPerPage = (isMobile = getIsMobile()) => {
    const mobileSlidesPerPage = storeSpacesCount === 1 ? 1 : 1.33;
    return isMobile ? mobileSlidesPerPage : 2.2555;
  };

  const { breadcrumbs, promoBanner } = storeContent.components;

  const storeSpaces = storeData?.spaces?.sort(
    (a, b) => a.displayOrder - b.displayOrder
  );

  const hasExperientialTemplate =
    storeData?.isExperiential && !storeData?.hybridExperientialStore;

  const getSpaceHoursSlugIndex = () => {
    const path = router.asPath;
    const hashIndex = path.indexOf("#");
    const spaceCafe = storeData?.spaces.find(
      (item) => item.spaceType === "eat"
    );

    if (hashIndex !== -1) {
      const spaceName = path.substring(hashIndex + 1);
      if (spaceName === "studio-hours") {
        return 1;
      }
      if (spaceName === "cafe-hours" && spaceCafe) {
        return 2;
      }
    }
    return 0;
  };

  const getSweatWithUsContent = () => {
    let updatedSweatWithUsContent = [];
    if (hasExperientialTemplate) {
      updatedSweatWithUsContent = [
        {
          ...storeContent?.components.sweatWithUs.experientialTemplate
            .studioClasses,
          imgUrl: storeData?.carousel[0].classes_images[0],
          href: storeData?.classesUrl
        },
        {
          ...storeContent?.components.sweatWithUs.experientialTemplate
            .communityEvents,
          imgUrl: storeData?.carousel[0].event_images[0],
          href: storeData?.eventsUrl
        }
      ];
      if (storeData?.data?.storeInformationById?.eventRentalEmailUrl) {
        updatedSweatWithUsContent = [
          ...updatedSweatWithUsContent,
          {
            ...storeContent?.components.sweatWithUs.experientialTemplate
              .eventsAndRentals,
            imgUrl: storeData?.carousel[0].event_rentals_images[0],
            href: storeData?.data?.storeInformationById?.eventRentalEmailUrl
          }
        ];
      }
      return updatedSweatWithUsContent;
    }
    const storeType = storeData?.isExperiential ? "experiential" : "regular";
    updatedSweatWithUsContent = storeContent?.components.sweatWithUs[storeType];
    if (storeData?.isExperiential && storeData?.classesUrl) {
      updatedSweatWithUsContent.cta.href = storeData?.classesUrl;
    }
    if (storeData?.isExperiential && storeData?.eventsUrl) {
      updatedSweatWithUsContent.featureBlocks[1].embeddedHref =
        storeData?.eventsUrl;
    }
    return updatedSweatWithUsContent;
  };

  const getSpaceHoursImageUrl = () => {
    const images = [
      storeData?.data?.storeInformationById?.storeSpaceImageUrl,
      storeData?.data?.storeInformationById?.studioSpaceImageUrl
    ];
    if (storeData?.data?.storeInformationById?.cafeSpaceImageUrl) {
      images.push(storeData?.data?.storeInformationById?.cafeSpaceImageUrl);
    }
    return images;
  };

  const hasFootwear = storeData?.amenities?.some(
    (amenity) => amenity === "footwear"
  );

  const hasAppointments = storeData?.abilities?.some(
    (ability) => ability === "PERSONAL_SHOPPING"
  );

  return (
    <>
      <main className={cs("lll-grid", styles.storeContainer)}>
        <section className={cs(styles.breadcrumbsContainer, "OneLinkNoTx")}>
          <Breadcrumbs
            seoItems={[{ url: "/stores", label: breadcrumbs.storesLabel }]}
            // eslint-disable-next-line no-unused-vars
            renderSEOToHead={(seoContent) => {}}
            ariaLabel="Breadcrumb"
          >
            <LinkGenerator href="/stores">
              {breadcrumbs.storesLabel}
            </LinkGenerator>
          </Breadcrumbs>
        </section>
        {storeData?.message && (
          <section className={styles.specialMessageContainer}>
            <div className={styles.specialMessageText}>
              {storeData?.message}
            </div>
          </section>
        )}
        {hasExperientialTemplate && (
          <section className={styles.storeContactUsContainer}>
            <ContactUs
              fullAddress={storeData?.fullAddress}
              latitude={storeData?.latitude}
              longitude={storeData?.longitude}
              email={storeData?.email}
              phone={storeData?.phone}
              isExperiential={storeData?.isExperiential}
            />
          </section>
        )}
        {hasExperientialTemplate ? (
          <section className={styles.sweatWithUsExperientialStoreContainer}>
            <SweatWithUsExperientialStore
              content={getSweatWithUsContent()}
              hasOneLinkTranslation={storeData?.hasOneLinkTranslation}
            />
          </section>
        ) : (
          <section className={styles.sweatWithUsContainer}>
            <SweatWithUs content={getSweatWithUsContent()} />
          </section>
        )}
        {storeSpacesCount > 0 && (
          <section className={styles.spacesContainer}>
            <SpacesGrid
              featuredAmenity="footwear"
              hasFeaturedAmenity={hasFootwear}
              storeSlug={slug}
              storeName={storeData?.name}
              storeAmenities={
                (!hasExperientialTemplate && storeData?.amenities) || []
              }
              spaces={{
                storeSpaces,
                ...(hasExperientialTemplate
                  ? storeContent.components.spacesGrid.experientialTemplate
                  : storeContent.components.spacesGrid.regular)
              }}
              slidesPerPage={getSlidesPerPage()}
              hasOneLinkTranslation={storeData?.hasOneLinkTranslation}
            />
          </section>
        )}

        <section className={styles.promoBannerContainer}>
          <PromoBanner
            heading={promoBanner.heading}
            blurb={promoBanner.blurb}
            button={{
              text: promoBanner.button.text,
              href: promoBanner.button.href,
              analyticsId: STORE_TOP_SELLERS_BANNER
            }}
            backgroundImage={promoBanner.backgroundImage}
          />
        </section>
      </main>
    </>
  );
};

/**
 * getInitialProps receives a single argument called context, it's an object with the following properties:
 *
 * pathname - Current route. That is the path of the page in /pages
 * query - Query string section of URL parsed as an object
 * asPath - String of the actual path (including the query) shown in the browser
 * req - HTTP request object (server only)
 * res - HTTP response object (server only)
 * err - Error object if any error is encountered during the rendering
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
Store.getInitialProps = async ({ query, req, res }) => {
  const validStoreStatuses = ["active", "active_soon", "active_conflict"];
  const { locale } = readQuery({ query, req });
  const slug = getSlug(query, req);
  const hasOneLinkTranslation =
    locale === "fr-CA" ||
    query?.locale === "fr_CA" ||
    query?.locale === "fr-CA" ||
    null;

  let storeData = {};
  let cardsData = {};
  let carouselData = {};
  let experientialStoreData = {};
  let optInFlowData = {};
  let is404 = false;
  let storeError;

  try {
    storeData = await fetchStoreBySlug(slug, req, res);

    const storeStatus =
      storeData?.data?.storeBySlug?.storeStatus?.toLowerCase();

    if (
      typeof storeStatus === "undefined" ||
      validStoreStatuses.indexOf(storeStatus) === -1
    ) {
      const urlLocale = locale.toLowerCase();
      const urlWithLocale =
        urlLocale === LocaleEnum["en-us"] ? "/stores" : `/${urlLocale}/stores`;
      redirect(urlWithLocale, res);
    }
  } catch (error) {
    storeError = error.message;
    is404 = true;
  }

  const cardIds = [
    "card_mirror",
    "card_bopis",
    "card_returns",
    "card_hemming",
    "card_online",
    "card_run",
    "card_yoga",
    "footer_subscribe",
    "footer_chat",
    "footer_exp_subscribe",
    "footer_exp_services"
  ];

  try {
    cardsData = await graphql({
      query: cardsQuery,
      variables: {
        contentIds: cardIds.join(",")
      },
      req,
      res,
      getSecret: req?.app?.locals?.getSecret
    });
  } catch (error) {
    throw new Error(`Error fetching cards: ${error.message}`);
  }

  try {
    carouselData = await graphql({
      query: carouselQuery,
      variables: {
        storeId: storeData?.data?.storeBySlug?.id
      },
      req,
      res,
      getSecret: req?.app?.locals?.getSecret
    });
  } catch (error) {
    throw new Error(`Error fetching carousel: ${error.message}`);
  }

  try {
    optInFlowData = await graphql({
      query: optInFlowQuery,
      variables: {
        contentIds: "lo_dsp_signup"
      },
      req,
      res,
      getSecret: req?.app?.locals?.getSecret
    });
  } catch (error) {
    throw new Error(`Error fetching LO/DSP signup data: ${error.message}`);
  }

  const cards = sortCards(cardsData?.data?.storeCards, cardIds) || [];
  const banners = sortCards(cardsData?.data?.storeBanners, cardIds) || [];
  const carousel = carouselData?.data?.storeCarousel || [];
  const optInFlowContent = optInFlowData?.data.loDspSignUp || {};

  const {
    name: storeName,
    city,
    state,
    isExperiential,
    id: storeId
  } = storeData?.data?.storeBySlug || {};

  const meta = storeMeta({
    storeName,
    city,
    state,
    slug,
    isExperiential
  });

  if (storeId) {
    const getSecret = req?.app?.locals?.getSecret;
    experientialStoreData = await graphql({
      query: experientialStoreQuery,
      variables: {
        storeId: storeId
      },
      req,
      res,
      getSecret
    });
  }

  return {
    slug,
    storeData: {
      ...storeData?.data?.storeBySlug,
      hasOneLinkTranslation,
      cards: cards.concat(banners),
      carousel: carousel,
      ...(experientialStoreData || {}),
      optInFlowContent: optInFlowContent
    },
    is404,
    storeError,
    meta
  };
};

Store.propTypes = {
  storeData: PropTypes.shape({
    isExperiential: PropTypes.bool,
    amenities: PropTypes.arrayOf(PropTypes.string),
    abilities: PropTypes.arrayOf(PropTypes.string),
    images: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        url: PropTypes.string,
        displayOrder: PropTypes.number,
        accessibilityText: PropTypes.string,
        tags: PropTypes.arrayOf(PropTypes.string)
      })
    ),
    hasOneLinkTranslation: PropTypes.bool,
    heroImage: PropTypes.shape({
      id: PropTypes.string,
      url: PropTypes.string,
      displayOrder: PropTypes.number,
      accessibilityText: PropTypes.string,
      tags: PropTypes.arrayOf(PropTypes.string)
    }),
    hours: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        openHour: PropTypes.string,
        closeHour: PropTypes.string
      })
    ),
    specialHours: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string,
        description: PropTypes.string,
        openHour: PropTypes.string,
        closeHour: PropTypes.string
      })
    ),
    spaces: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        about: PropTypes.string,
        aboutHtml: PropTypes.string,
        amenities: PropTypes.arrayOf(PropTypes.string),
        bookingEmail: PropTypes.string,
        displayOrder: PropTypes.number,
        hours: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            openHour: PropTypes.string,
            closeHour: PropTypes.string
          })
        ),
        longDescription: PropTypes.string,
        name: PropTypes.string,
        shortDescription: PropTypes.string
      })
    ),
    id: PropTypes.string,
    city: PropTypes.string,
    classesUrl: PropTypes.string,
    country: PropTypes.string,
    email: PropTypes.string,
    eventsUrl: PropTypes.string,
    facebookUrl: PropTypes.string,
    facebookUsername: PropTypes.string,
    friendlyFromName: PropTypes.string,
    footwear: PropTypes.bool,
    fullAddress: PropTypes.string,
    instagramHandle: PropTypes.string,
    instagramUrl: PropTypes.string,
    latitude: PropTypes.number,
    liveChatUrl: PropTypes.string,
    longitude: PropTypes.number,
    message: PropTypes.string,
    name: PropTypes.string,
    phone: PropTypes.string,
    storeStatus: PropTypes.string,
    slug: PropTypes.string,
    state: PropTypes.string,
    storeNumber: PropTypes.string,
    storeType: PropTypes.string,
    storeSpaceImageUrl: PropTypes.string,
    studioSpaceImageUrl: PropTypes.string,
    cafeSpaceImageUrl: PropTypes.string,
    twitterHandle: PropTypes.string,
    twitterUrl: PropTypes.string,
    foodOrderUrl: PropTypes.string,
    hasMirror: PropTypes.bool,
    fuelMenu: PropTypes.string,
    eventRentalsUrl: PropTypes.string,
    subHeader: PropTypes.string,
    classesImageUrl: PropTypes.string,
    eventsImageUrl: PropTypes.string,
    eventRentalsImageUrl: PropTypes.string,
    hybridExperientialStore: PropTypes.bool,
    usrLocale: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    waitwhileServiceId: PropTypes.string,
    waitwhileFootwearId: PropTypes.string
  }),
  meta: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    canonicalPath: PropTypes.string,
    robots: PropTypes.string
  }),
  slug: PropTypes.string
};

Store.defaultProps = {
  storeData: {},
  slug: "",
  meta: {}
};

export default Store;
